import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Heart, ExternalLink, Check, Home, Share, Copy } from 'lucide-react';
import { MODAL_BG } from '../assets/modalBg';
import KarmanaRegistrationForm from './KarmanaRegistrationForm';
import GoogleFormEmbed from './GoogleFormEmbed';
import { triggerGoogleFormsSync } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isStandalone, isIOS } from '../utils/push';

// WhatsApp icon (matches ActivityJourney WAIcon)
function WAIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M11.5 2C6.253 2 2 6.252 2 11.5c0 1.786.483 3.461 1.32 4.902L2 22l5.735-1.303A9.477 9.477 0 0011.5 22c5.247 0 9.5-4.253 9.5-9.5S16.747 2 11.5 2zm0 17.4a7.88 7.88 0 01-4.02-1.098l-.289-.172-2.985.679.715-2.9-.188-.298A7.877 7.877 0 013.6 11.5c0-4.355 3.544-7.9 7.9-7.9s7.9 3.545 7.9 7.9-3.544 7.9-7.9 7.9z"/>
    </svg>
  );
}

// "Done" dot — filled green circle with checkmark (matches ActivityJourney done state)
function StepDotDone({ delay }) {
  return (
    <div
      style={{
        width: 16, height: 16, flexShrink: 0, borderRadius: '50%',
        backgroundColor: '#16a34a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: `sm-dot-in 0.2s ease ${delay}s forwards`,
        opacity: 0,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}

// "Current" dot — white circle with green border + glow (matches ActivityJourney current state)
function StepDotCurrent({ delay }) {
  return (
    <div
      style={{
        width: 16, height: 16, flexShrink: 0, borderRadius: '50%',
        border: '2px solid #22c55e',
        background: 'white',
        boxShadow: '0 0 0 3px rgba(34,197,94,0.2), 0 0 8px rgba(34,197,94,0.35)',
        animation: `sm-dot-in 0.2s ease ${delay}s forwards`,
        opacity: 0,
      }}
    />
  );
}

// fade-in from slightly above
const fi = (delay, dur = 0.32) => ({
  animation: `sm-fade-in ${dur}s ease ${delay}s forwards`,
  opacity: 0,
});


/**
 * Shared journey-success view — used for both "You're Interested" (no-reg)
 * and "You're Registered" (after Karmana form) paths.
 */
function SuccessJourneyView({ activityName, step1Label, step1Sub, whatsAppInviteUrl, modalCopied, handleCopyLink }) {
  return (
    <div className="pt-5 pb-4 px-5">
      {/* Activity name — centered */}
      <p
        className="text-center font-bold text-gray-800 mb-4 px-2 leading-snug"
        style={{
          fontSize: 16,
          ...fi(0),
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        data-testid="success-activity-name"
      >
        {activityName}
      </p>

      {/* ── Step 1: "You're Registered" or "You're Interested" (done dot) ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 20, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch', paddingTop: 3 }}>
          <StepDotDone delay={0.3} />
          <div style={{ flex: 1, width: 1, minHeight: 32, backgroundColor: '#16a34a', transformOrigin: 'top center', animation: 'sm-line-expand 0.35s ease 0.6s both', transform: 'scaleY(0)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingBottom: 28 }}>
          <p className="text-xl font-bold text-green-700" style={fi(0.5, 0.3)}>
            {step1Label}
          </p>
          {step1Sub && (
            <div style={fi(0.65, 0.3)}>
              {step1Sub}
            </div>
          )}
        </div>
      </div>

      {/* ── Step 2: Bring Your Friends (current dot) + Invite/Copy CTAs ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 20, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch', paddingTop: 3 }}>
          <StepDotCurrent delay={0.85} />
          <div style={{ flex: 1, width: 1, minHeight: 20, backgroundColor: '#e5e7eb', transformOrigin: 'top center', animation: 'sm-line-expand 0.4s ease 1.5s both', transform: 'scaleY(0)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, paddingBottom: 20 }}>
          <p className="text-xl font-bold text-green-700" style={fi(1.0, 0.3)}>Bring Your Friends</p>
          <div className="flex flex-col gap-1.5 flex-shrink-0" style={{ ...fi(1.0, 0.3), marginTop: 2 }}>
            <a
              href={whatsAppInviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-green-600 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap min-w-[88px]"
              data-testid="success-modal-invite-btn"
            >
              <WAIcon /> Invite
            </a>
            <button
              onClick={handleCopyLink}
              className="flex items-center justify-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap min-w-[88px]"
              data-testid="success-modal-copy-link-btn"
            >
              {modalCopied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
              {modalCopied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Step 3: Grey pending section — Participate / Share / Inspire ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 20, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 3 }}>
          <div style={{ width: 14, height: 14, flexShrink: 0, borderRadius: '50%', background: '#d1d5db', border: '1px solid #e5e7eb', animation: 'sm-dot-in 0.2s ease 1.95s forwards', opacity: 0 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingBottom: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5, ...fi(2.15, 0.25) }}>Participate</span>
            <span style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5, ...fi(2.5, 0.25) }}>Share Your Experience</span>
            <span style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5, ...fi(2.85, 0.25) }}>Inspire Others</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ActivitySuccessModal
 *
 * Karmana: "You're Almost There" header + registration form.
 * External-link: animated "You're Interested in" → "One more step".
 * No-registration: Activity name → left-side timeline (dot-line-dot) → "You're Interested" → "Bring Your Friends" + Invite/Copy CTAs.
 * Bottom panel: Karmana Journeys logo + text + Add to Home Page CTA.
 */
export default function ActivitySuccessModal({
  activityName,
  registrationLink,
  registrationEndDate,
  isKarmana,
  registrationFull,
  registrationFields,
  registrationRequirement,
  alreadyRegistered,
  user,
  activityId,
  activitySlug,
  embedUrl = null,
  onRegisterClick,
  onClose,
  onRegistered,
  onSignIn,
  onOTPRequired,
  sourceProps = {},
  onFieldsChange,
}) {
  const [regDone, setRegDone] = useState(alreadyRegistered || false);
  const [modalCopied, setModalCopied] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const { user: authUser, openAuthModal } = useAuth();
  const isInStandalone = useMemo(() => isStandalone(), []);
  const isOnIOS = useMemo(() => isIOS(), []);

  // Lock body scroll when modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Capture deferred beforeinstallprompt event
  useEffect(() => {
    const stored = window.__karmana_pwa_prompt;
    if (stored) setInstallPromptEvent(stored);
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      window.__karmana_pwa_prompt = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const activityUrl = `${window.location.origin}/activities/${activitySlug || activityId}`;
  const whatsAppInviteUrl = `https://wa.me/?text=${encodeURIComponent(`Join me at "${activityName}" on Karmana! ${activityUrl}`)}`;

  const handleModalCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(activityUrl);
      setModalCopied(true);
      setTimeout(() => setModalCopied(false), 2000);
    } catch {}
  }, [activityUrl]);

  const handlePWAInstall = useCallback(() => {
    if (isOnIOS) { setShowIOSGuide(true); return; }
    const prompt = installPromptEvent || window.__karmana_pwa_prompt;
    if (prompt) { prompt.prompt(); }
  }, [isOnIOS, installPromptEvent]);

  const handleRegSuccess = () => {
    setRegDone(true);
    if (onRegistered) onRegistered();
  };

  const handleFormSubmitted = async () => {
    try { await triggerGoogleFormsSync(activityId); } catch { /* 15-min cycle fallback */ }
    if (authUser) {
      toast.success('Registration submitted! Your spot will be confirmed shortly.', { duration: 5000 });
      setTimeout(() => { setRegDone(true); onRegistered?.(); }, 5000);
    } else {
      openAuthModal('form_submitted');
    }
  };

  const handleSignIn = (newUser) => {
    // For Karmana activities, sign-in means OTP was completed after form submission → show success view
    if (isKarmana) setRegDone(true);
    if (onSignIn) onSignIn(newUser);
  };

  return (
    <>
      <style>{`
        @keyframes sm-fade-in {
          from { opacity: 0; transform: translateY(-9px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes sm-dot-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes sm-line-expand {
          from { transform: scaleY(0); }
          to   { transform: scaleY(1); }
        }
        @keyframes karmana-pulse {
          0%,100% { transform: scale(1); }
          50%     { transform: scale(1.07); }
        }
        @keyframes sm-form-reveal {
          from { clip-path: inset(0 0 100% 0); opacity: 0; }
          to   { clip-path: inset(0 0 0% 0); opacity: 1; }
        }
      `}</style>

      {/* ── iOS install guide sheet ── */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowIOSGuide(false)}
          data-testid="ios-guide-overlay"
        >
          <div
            className="w-full max-w-sm bg-white rounded-t-2xl px-6 pt-5 pb-8 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-gray-900 text-base">Add to Home Screen</p>
              <button onClick={() => setShowIOSGuide(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">1</span>
                <span>Tap the <Share size={14} className="inline mx-0.5 text-blue-500" /> <strong>Share</strong> button at the bottom of Safari</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">2</span>
                <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">3</span>
                <span>Tap <strong>"Add"</strong> in the top right corner</span>
              </li>
            </ol>
            <button
              onClick={() => setShowIOSGuide(false)}
              className="mt-5 w-full py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Overlay ── */}
      <div
        className="fixed inset-0 z-[150] bg-black/50 flex justify-center items-start px-4 pt-[72px] pb-6 overflow-y-auto"
        data-testid="activity-success-modal"
      >

        {/* ── Card ── */}
        <div
          className="relative shadow-xl overflow-hidden w-full max-w-sm md:max-w-[624px]"
          style={{
            backgroundColor: '#f7f5f0',
            backgroundImage: `url(${MODAL_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="success-modal-card"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-300 hover:text-gray-500 transition-colors z-10"
            data-testid="success-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {/* ── Scrollable content ── */}
          <div className="flex-1 overflow-y-auto">

            {isKarmana ? (
              /* ── Karmana path ── */
              regDone ? (
                /* Phase 3: Post-registration journey view */
                <SuccessJourneyView
                  activityName={activityName}
                  step1Label="You're Registered"
                  whatsAppInviteUrl={whatsAppInviteUrl}
                  modalCopied={modalCopied}
                  handleCopyLink={handleModalCopyLink}
                />
              ) : (
                <>
                  {/* Phase 1: Header — appears instantly */}
                  <div className="text-center pt-6 pb-3 px-6">
                    <h2 className="text-2xl font-bold text-gray-900">You&apos;re Almost There</h2>
                    <p className="text-gray-700 text-base mt-1">
                      <strong>Register</strong> to Join the Activity
                    </p>
                  </div>
                  {/* Phase 2: Form — top-to-bottom reveal over 1.5 s */}
                  <div
                    className="px-6 pb-4"
                    style={{
                      animation: 'sm-form-reveal 1.5s ease 0.15s forwards',
                      clipPath: 'inset(0 0 100% 0)',
                      opacity: 0,
                    }}
                  >
                    <p className="text-sm font-semibold text-gray-700 mb-3 text-left">
                      Provide following details to register
                    </p>
                    <KarmanaRegistrationForm
                      activityId={activityId}
                      fields={registrationFields || []}
                      user={user}
                      registrationRequirement={registrationRequirement}
                      onSuccess={handleRegSuccess}
                      onSignIn={handleSignIn}
                      onOTPRequired={onOTPRequired}
                      sourceProps={sourceProps}
                      onFieldsChange={onFieldsChange}
                      disabled={!!registrationFull}
                      registrationFull={!!registrationFull}
                    />
                  </div>
                </>
              )

            ) : (
              /* ── Non-Karmana path ── */
              embedUrl ? (
                /* Google Forms embed */
                <div className="px-3 pb-4" data-testid="success-embedded-form">
                  <div className="flex items-center justify-center gap-2 py-3" style={fi(0)}>
                    <Heart size={22} fill="#16a34a" stroke="#16a34a" />
                    <span className="text-base font-semibold text-gray-700">Register for {activityName}</span>
                  </div>
                  <GoogleFormEmbed
                    embedUrl={embedUrl}
                    onSubmitted={handleFormSubmitted}
                  />
                </div>

              ) : registrationLink ? (
                /* External registration link: Journey-style view */
                <SuccessJourneyView
                  activityName={activityName}
                  step1Label="You're Interested"
                  step1Sub={
                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">
                      Complete Organiser&apos;s Registration{' '}
                      <a
                        href={registrationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onRegisterClick?.()}
                        className="inline-flex items-center text-blue-500 hover:text-blue-700"
                        data-testid="success-registration-link"
                      >
                        <ExternalLink size={11} className="inline ml-0.5" />
                      </a>
                    </p>
                  }
                  whatsAppInviteUrl={whatsAppInviteUrl}
                  modalCopied={modalCopied}
                  handleCopyLink={handleModalCopyLink}
                />

              ) : (
                /* ── No-registration path: shared journey view ── */
                <SuccessJourneyView
                  activityName={activityName}
                  step1Label="You're Interested"
                  whatsAppInviteUrl={whatsAppInviteUrl}
                  modalCopied={modalCopied}
                  handleCopyLink={handleModalCopyLink}
                />
              )
            )}
          </div>

          {/* ── Bottom panel: Karmana Journeys logo + Add to Home Page CTA ── */}
          <div className="flex-shrink-0 flex items-center justify-between gap-2 py-3 px-5 border-t border-gray-200/60 bg-white/60 backdrop-blur-sm">
            <div className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/images/karmana-journey-logo.png"
                alt="Karmana Journeys"
                className="w-7 h-7 rounded-full object-contain flex-shrink-0"
              />
              <span className="text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">Karmana Journeys</span>
            </div>
            {!isInStandalone && (
              <button
                onClick={handlePWAInstall}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold text-white bg-gray-700 hover:bg-gray-800 transition-colors whitespace-nowrap flex-shrink-0"
                data-testid="modal-pwa-install-btn"
                aria-label="Add Karmana Journeys to home screen"
              >
                <Home size={10} strokeWidth={2.5} />
                Add to Home Page
              </button>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
